import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { AppConstants } from '@shared-config/appConstants';
import { AppHelper } from '@shared-helpers/appHelper';
import { DateConverter } from '@shared-helpers/dateConverter';
import { MemberModel } from '@shared-model/member/MemberModel';
import { ProductModel } from '@shared-model/products/ProductModel';
import { ProductType } from '@shared-model/products/ProductType';
import { BaseServiceOperationResult } from '@shared-results/BaseServiceOperationResult';
import { MemberDisplayNameResult } from '@shared-results/member/MemberDisplayNameResult';
import { MemberDataResult } from '@shared-results/member/MemberResult';
import { ServiceOperationOutcome } from '@shared-results/ServiceOperationOutcome';
import { ValidationOutcome } from '@shared-results/ValidationOutcome';
import { BasePollingService } from '@shared-services/base-polling-service';
import { Observable, Observer, Subscription } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root' // Single instance provided in root
})
export class MemberService extends BasePollingService<MemberDataResult, MemberModel> {
  private memberUrl = '/member/';

  member: WritableSignal<MemberModel> = signal(null, { equal: AppHelper.objectsEqual });
  memberLoggedIn: Signal<boolean> = computed(() => this.member()?.IsLoggedIn, { equal: AppHelper.objectsEqual });
  memberUserProducts: Signal<ProductModel[]> = computed(() => this.member()?.UserProducts, { equal: AppHelper.objectsEqual });
  memberStandardProducts: Signal<ProductModel[]> = computed(() => this.member()?.StandardProducts, { equal: AppHelper.objectsEqual });  
  memberFavouriteExpertIds: Signal<number[]> = computed(() => this.member()?.FavouriteExpertIds, { equal: AppHelper.objectsEqual });
  memberNotificationAlertExpertIds: Signal<number[]> = computed(() => this.member()?.NotificationAlertExpertIds, { equal: AppHelper.objectsEqual });
  
  constructor() { super("MemberService"); }

  protected override getPollingObservable(): Observable<MemberDataResult> {
    let url: string = `${this.memberUrl}getMember`;
    let options = this.httpOptions;

    return this.http?.post<MemberDataResult>(url, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpErrorAndRethrow<MemberDataResult>(url, null, options, this)),
      switchMap((result) => {
        return this.handleOperationResult(url, null, options, result, {
          additionalSuccessCriteria: (result) => { return result?.MemberData ? true : false },
          throwIfNotSuccess: true
        })
      }),
    );
  }

  protected override getPollingInterval(): number {
    return AppConstants.memberPollingInterval;
  }

  protected override onPollingError(error: any): void { }

  protected override onPollingResult(result: MemberDataResult): MemberModel {
    return result?.MemberData;
  }

  protected override getPollingCachedData(): MemberModel {
    return this.member();
  }

  protected override setPollingCachedData(data: MemberModel): void {
    this.cacheMemberData(data);
  }

  getMember(observerOrNext?: Partial<Observer<MemberModel>> | ((value: MemberModel) => void)): Subscription {
    return this.getPollingData(observerOrNext);
  }

  refreshMember(observerOrNext?: Partial<Observer<MemberModel>> | ((value: MemberModel) => void)): Subscription {
    return this.refreshPollingData(observerOrNext);
  }

  cacheMemberData(member: MemberModel): void {
    if (member) {
      // Only cache if non empty
      this.member.set(member);
    }
  }

  cacheMemberQueueExpertId(queueExpertId: number): void {
    if (!this.moduleConfig?.ExpertsSupported) return;
    // Cache to member object as persists for a number of hours
    // Get current member data
    let currentMember: MemberModel = AppHelper.cloneObject(this.member());
    if (currentMember) {
      // Update modified fields
      currentMember.QueueExpertId = queueExpertId;
      // Set modified data
      this.cacheMemberData(currentMember);
    }
  }

  setFavourite(expertId: number, isFavourite: boolean, observerOrNext?: Partial<Observer<BaseServiceOperationResult>> | ((value: BaseServiceOperationResult) => void)): void {
    if (!this.moduleConfig?.ExpertsSupported) return;
    let url: string = `${this.memberUrl}SetFavouriteByExpertId`;
    let body: string = JSON.stringify({ expertId: expertId, isFavourite: isFavourite });
    let options = this.httpOptions;
    let client = this.http?.post<BaseServiceOperationResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<BaseServiceOperationResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success) {
        // Request succeeded
        // Get current member data
        let currentMember: MemberModel = AppHelper.cloneObject(this.member());
        if (currentMember) {
          if (isFavourite) {
            currentMember.FavouriteExpertIds = AppHelper.arrayAddIfNotExists(currentMember.FavouriteExpertIds, expertId);
          }
          else {
            currentMember.FavouriteExpertIds = AppHelper.arrayRemoveIfExists(currentMember.FavouriteExpertIds, expertId);
          }
          // Set modified data
          this.cacheMemberData(currentMember);
        }
      }
    });
  }

  setDisplayName(displayName: string, observerOrNext?: Partial<Observer<MemberDisplayNameResult>> | ((value: MemberDisplayNameResult) => void)): void {
    if (!this.moduleConfig?.ExpertsSupported) return;
    let url: string = `${this.memberUrl}SetDisplayName`;
    let body: string = JSON.stringify({ displayName: displayName });
    let options = this.httpOptions;
    let client = this.http?.post<MemberDisplayNameResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<MemberDisplayNameResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success && result.DisplayNameValidationOutcome == Number(ValidationOutcome.Valid) && !result.DisplayNameIsAlreadyTaken) {
        // Request succeeded
        // Get current member data
        let currentMember: MemberModel = AppHelper.cloneObject(this.member());
        if (currentMember) {
          currentMember.DisplayName = displayName;
          // Set modified data
          this.cacheMemberData(currentMember);
        }
      }
    });
  }

  filterProductsByTypeAndTariff(products: ProductModel[], productTypeIds: number[], expertTariffTypeId: number, maximumCount?: number): ProductModel[] {
    return this.productListByTypeAndTariff(products, productTypeIds, expertTariffTypeId, maximumCount);
  }

  getBestOfferProduct(productTypeIds: number[]): ProductModel {
    return this.bestOfferProductByType(this.memberUserProducts(), productTypeIds);
  }

  getBestProduct(productTypeIds: number[], expertTariffTypeId: number): ProductModel {
    return this.bestProductByTypeAndTariff(this.memberUserProducts(), productTypeIds, expertTariffTypeId);
  }

  // Return provided product from the user product list for the current member
  // Expected usage is to provide a product from the standard products list and return an object with any member specific offers included i.e. from the user product list
  getMemberProductFromStandard(product: ProductModel): ProductModel {
    if (product) {
      // Find all entries in UserProducts with matching ProductId, sort by price per minute and then take the first one. Default to product passed in if none found
      return this.member()?.UserProducts?.filter((userProduct) => userProduct.ProductId == product.ProductId)?.sort((a, b) => a.PricePerMinute?.Amount - b.PricePerMinute?.Amount)?.find(() => true) ?? product;
    }
    return product;
  }

  availableMinutesForProduct(product: ProductModel): number {
    let memberData = this.member();
    if (memberData?.IsLoggedIn && product) {
      // Member is logged in and we have product data
      // Calculate number of whole minutes available using AvailableBalance
      return (product?.PricePerMinuteStandard?.Amount > 0) ? Math.floor((memberData?.AvailableBalance?.Amount ?? 0) / product?.PricePerMinuteStandard?.Amount) : 0;
    }
    return null;
  }

  private bestOfferProductByType(products: ProductModel[], productTypeIds: number[]): ProductModel {
    return products?.filter(
      (product) => productTypeIds?.includes(product?.ProductTypeId) && product?.OfferId // Select all products with a matching type and with and offer
    )?.sort(
      this.moduleConfig?.ProductSortBestOffer // Sort using the sort function provided
    )?.find(() => true);
  }

  private bestProductByTypeAndTariff(products: ProductModel[], productTypeIds: number[], expertTariffTypeId: number): ProductModel {
    return products?.filter(
      (product) => productTypeIds?.includes(product?.ProductTypeId) && product?.ExpertTariffTypeId == expertTariffTypeId // Select all products with a matching type and tariff
    )?.sort(
      this.moduleConfig?.ProductSortBestOffer // Sort using the sort function provided
    )?.find(() => true);
  }

  private productListByTypeAndTariff(products: ProductModel[], productTypeIds: number[], expertTariffTypeId: number, maximumCount?: number): ProductModel[] {
    if (!!(expertTariffTypeId) // Tariff parameter set
      && !(products?.filter(p => !!(p.ExpertTariffTypeId))?.find(() => true)) // No products with tariffs in the list
    ) {
      // We don't have any products with tariffs set, so set tariff parameter to null
      expertTariffTypeId = null;
    }

    if (maximumCount > 0) {
      // Return products that match, but only up to a maximum amount, need to sort in order to get priority items first
      let isPriorityProduct = (product: ProductModel) => {
        // Prioritise showing PAYG products if there are any
        return (product?.ProductTypeId == ProductType.IvrPaygSession || product?.ProductTypeId == ProductType.ImPaygSession) ? 1 : 0;
      };

      return products?.filter(
        (product) =>
          productTypeIds?.includes(product?.ProductTypeId) // Match product type id
          && (
            !!(expertTariffTypeId) ?
              expertTariffTypeId == product?.ExpertTariffTypeId // Tariff parameter set, match on it
              : !(product?.ExpertTariffTypeId) // Tariff parameter not set, match on it not being set i.e. find products without it set
          )
      ).sort(
        (a, b) => isPriorityProduct(b) - isPriorityProduct(a) || a.PriceStandard?.Amount - b.PriceStandard?.Amount // Sort by priority product then standard price
      ).slice(
        0, maximumCount // Take maximum number from list
      ).sort(
        (a, b) => a.SortOrder - b.SortOrder // Sort remaining items by sort order
      );
    }
    else {
      // Return all products that match
      return products?.filter((product) =>
        productTypeIds?.includes(product?.ProductTypeId) // Match product type id
        && (
          !!(expertTariffTypeId) ?
            expertTariffTypeId == product?.ExpertTariffTypeId // Tariff parameter set, match on it
            : !(product?.ExpertTariffTypeId) // Tariff parameter not set, match on it not being set i.e. find products without it set
        )
      ).sort((a, b) => a.SortOrder - b.SortOrder);
    }
  }

  setEmailAlert(expertId: number, observerOrNext?: Partial<Observer<BaseServiceOperationResult>> | ((value: BaseServiceOperationResult) => void)): void {
    if (!this.moduleConfig?.ExpertsSupported) return;
    let url: string = `${this.memberUrl}SetEmailAlert`;
    let body: string = JSON.stringify({ expertId: expertId });
    let options = this.httpOptions;
    let client = this.http?.post<BaseServiceOperationResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<BaseServiceOperationResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success) {
        // Request succeeded
        // Get current member data
        let currentMember: MemberModel = AppHelper.cloneObject(this.member());
        if (currentMember) {
          currentMember.NotificationAlertExpertIds = AppHelper.arrayAddIfNotExists(currentMember.NotificationAlertExpertIds, expertId);
          // Set modified data
          this.cacheMemberData(currentMember);
        }
      }
    });
  }

  removeEmailAlert(expertId: number, observerOrNext?: Partial<Observer<BaseServiceOperationResult>> | ((value: BaseServiceOperationResult) => void)): void {
    if (!this.moduleConfig?.ExpertsSupported) return;
    let url: string = `${this.memberUrl}RemoveEmailAlert`;
    let body: string = JSON.stringify({ expertId: expertId });
    let options = this.httpOptions;
    let client = this.http?.post<BaseServiceOperationResult>(url, body, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpError<BaseServiceOperationResult>(url, body, options, this))
    );
    this.addSubscriptions(client, observerOrNext, (result) => {
      if (result?.ServiceOperationOutcome == ServiceOperationOutcome.Success) {
        // Request succeeded
        // Get current member data
        let currentMember: MemberModel = AppHelper.cloneObject(this.member());
        if (currentMember) {
          currentMember.NotificationAlertExpertIds = AppHelper.arrayRemoveIfExists(currentMember.NotificationAlertExpertIds, expertId);
          // Set modified data
          this.cacheMemberData(currentMember);
        }
      }
    });
  }
}
